import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { keyframes } from '@emotion/react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Elements } from '@stripe/react-stripe-js';
import { Link, useNavigate } from 'react-router-dom';
import { stripePromise } from '../../utils/stripe';
import { SUBSCRIPTION_DISCOUNT_PERCENT } from '../../utils/subscriptionDiscounts';
import { track } from '../../utils/track';
import SiteHeader from '../Home/DynamicHomepage/components/Landing/SiteHeader';
import RaffleFooter from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleFooter/RaffleFooter';
import { GiveawayCountdownTimer } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleHero/GiveawayCountdownTimer';
import RaffleStripeCheckoutForm from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RaffleStripeCheckoutForm';
import RaffleEntryForm from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RaffleForms/RaffleEntryForm';
import type { RafflePaymentPayload } from '../Home/DynamicHomepage/components/NewLayout/RafflePage/RafflePaymentPage/RafflePaymentPage';
import GiveawayOfferSocialLinks from '../GiveawayOffer/GiveawayOfferSocialLinks';
import {
  PAGE_BG,
  CYAN,
  GOLD,
  INK,
  BODY,
  MUTED,
  LINE,
  kickerSx,
  ctaSx,
  panelSx
} from '../GiveawayOffer/giveawayOfferTheme';
import {
  drawWhen,
  formatAud,
  formatDrawDate,
  plural,
  promoCopy,
  promoPackItem,
  promoPackReceipt,
  rewardParts,
  savePromoPackReceipt,
  useJackpot,
  usePromoWinners,
  usePromotion,
  withUpsell,
  type PromoPackReceipt,
  type RewardParts
} from './promoPack';
import PromoUpsellStep from './PromoUpsellStep';
import PreviousPromoWinners from './PreviousPromoWinners';
import { recallUpsellToken } from '../../utils/upsellToken';

/* `/promo` — this week's promo pack, SOLD to visitors who are not signed in.
 *
 * THE CHECKOUT IS THE GIVEAWAY'S ONE-OFF PACK CHECKOUT, unchanged: RaffleEntryForm
 * takes the details (it creates no account and asks the server nothing about the
 * address), then RaffleStripeCheckoutForm in `standalone` mode posts
 * `/checkout/create-standalone-payment-intent` itself. The server finds or creates
 * the account BY EMAIL, charges a card typed here — never a saved one — and
 * fulfils off the webhook. So a member types their details like anybody else,
 * and nothing on this page reveals whether an address is already ours.
 *
 * NOTHING HERE GRANTS ANYTHING, and the copy must not say it has: entries are
 * credited when the payment settles (the webhook), and the cards are opened in
 * the member portal. A paid purchase lands on `/promo/confirmation` — this
 * funnel's own page, NOT the raffle upsell page.
 *
 * Built like the QR funnel's confirmation page (`GiveawayOffer`), whose dark
 * checkout dialog this copies; its palette is imported, not copied.
 */

/* Stripe draws its fields in an iframe our CSS cannot reach, so the dark
 * treatment is declared here — the QR funnel's dark checkout, value for value.
 * 16px text, or iOS zooms in on the card field. */
const ELEMENTS_APPEARANCE = {
  theme: 'night' as const,
  variables: {
    fontSizeBase: '16px',
    colorPrimary: CYAN,
    colorBackground: '#0d141d',
    colorText: INK,
    colorTextSecondary: MUTED,
    colorDanger: '#ff8b8b',
    borderRadius: '10px'
  },
  rules: {
    '.Label': { color: MUTED, fontWeight: '700' },
    '.Input': { borderColor: 'rgba(140,170,190,0.22)' },
    '.Input:focus': { borderColor: CYAN, boxShadow: 'none' }
  }
};

const labelSx = {
  fontSize: 12.5,
  fontWeight: 800,
  letterSpacing: '0.16em',
  color: MUTED,
  mb: 1.5
} as const;

/* ── this week's rewards ─────────────────────────────────────────────────────
 * They belong to the promo draw, not the pack, and reset when it is drawn: until
 * the admin sets the next week's, the page says "Rewards to be announced" — there
 * is no default. They are what the draw offers; nothing here says they have been
 * given or credited to anyone. */

/* The landing page's giveaway green, for the one button on the page. */
const BUY_GREEN = '#34d399';

/* One thing a pack gives: the figure large in the colour of what it is (cyan
   for the cards, gold for entries — the reward tiles' colours), the words small
   beneath, the way the tiles put "100,000" over "INC TOKENS". Side by side with
   a hairline between. */
const PackFigure = ({ colour, figure, label }: { colour: string; figure: string; label: string }) => (
  <Box
    sx={{
      px: { xs: 1, md: 1.5 },
      py: { xs: 1.5, md: 1.75 },
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      textAlign: 'center',
      minWidth: 0,
      '& + &': { borderLeft: `1px solid ${LINE}` }
    }}
  >
    <Typography sx={{ fontSize: { xs: 24, md: 28 }, fontWeight: 900, lineHeight: 1, color: colour, fontVariantNumeric: 'tabular-nums' }}>
      {figure}
    </Typography>
    <Typography
      sx={{
        mt: 0.75,
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: MUTED,
        lineHeight: 1.35,
        textWrap: 'balance'
      }}
    >
      {label}
    </Typography>
  </Box>
);
/* Whose they are. The rewards sit beside "Buy now", so without this line they
   read as something in every pack; they are the promo draw's prizes. */
const DRAW_NOTE = 'Every pack bought this week is an entry in the promo draw.';

/* The rewards as tiles, the way the owner asked for them: the tokens on the
 * left as one big gold figure, the pack on the right with the pack's own art,
 * and the custom line beneath. `large` is the no-picture layout, where the
 * rewards are the page's focus and both tiles are made to catch the eye — the
 * tokens most of all: a coin, a shining figure, a gold glow. The strip under a
 * picture uses the calm small tiles. Every animation stops for
 * prefers-reduced-motion. */
const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-7px); }
`;
const shine = keyframes`
  0% { background-position: 0% 50%; }
  100% { background-position: 200% 50%; }
`;
const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
`;
const STILL = { '@media (prefers-reduced-motion: reduce)': { animation: 'none' } };

const tileSx = (large: boolean) => ({
  position: 'relative' as const,
  minWidth: 0,
  // the tile is a size container, so the tokens figure can be sized from the
  // room the tile actually has (cqw) rather than from a breakpoint guess
  containerType: 'inline-size' as const,
  // the two share a row and the pack's art makes its tile the taller: each
  // centres its contents vertically, so the tokens never sit at the top
  display: 'flex',
  flexDirection: 'column' as const,
  justifyContent: 'center',
  p: large ? { xs: 2.5, md: 3 } : 1.25,
  borderRadius: large ? '18px' : '14px',
  border: '1px solid rgba(56,232,255,0.22)',
  background: 'rgba(56,232,255,0.05)',
  textAlign: 'center' as const,
  overflow: 'hidden'
});

/* A coin, drawn in CSS: a gold disc with a rim, "INC" struck in the middle. */
const Coin = ({ size }: { size: number }) => (
  <Box
    aria-hidden
    sx={{
      position: 'relative',
      width: size,
      height: size,
      mx: 'auto',
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      background:
        'radial-gradient(circle at 35% 28%, #fff6c4 0%, #ffd34d 30%, #f2b400 55%, #b97c00 80%, #6e4700 100%)',
      boxShadow:
        '0 22px 44px -16px rgba(242,180,0,0.85), inset 0 -7px 16px rgba(0,0,0,0.38), inset 0 7px 16px rgba(255,255,255,0.42)',
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: '11%',
        borderRadius: '50%',
        border: '2px solid rgba(255,248,214,0.55)',
        boxShadow: 'inset 0 0 0 3px rgba(110,71,0,0.35)'
      },
      animation: `${float} 5.5s ease-in-out infinite`,
      ...STILL
    }}
  >
    <Typography
      component="span"
      sx={{
        fontSize: size * 0.3,
        fontWeight: 950,
        lineHeight: 1,
        letterSpacing: '0.04em',
        color: '#5a3900',
        textShadow: '0 1px 0 rgba(255,255,255,0.45)'
      }}
    >
      INC
    </Typography>
  </Box>
);

/* The figure never wraps — "250,0 / 00" is not a number anyone can read — so
 * its size steps down with its length instead, and on a phone a long figure
 * takes the whole row rather than half of one. */
const tokenFigureSize = (figure: string, large: boolean) => {
  const n = figure.length;
  if (!large) return n <= 6 ? 26 : n <= 8 ? 22 : 18;
  if (n <= 4) return { xs: 48, md: 64 };
  if (n <= 6) return { xs: 40, md: 54 };
  if (n <= 8) return { xs: 40, md: 44 };
  if (n <= 9) return { xs: 34, md: 36 };
  return { xs: 30, md: 30 };
};

const RewardTiles = ({ parts, large }: { parts: RewardParts; large: boolean }) => {
  const figure =
    parts.tokens !== null
      ? parts.tokens.toLocaleString('en-AU', { maximumFractionDigits: 2 })
      : '';
  const both = parts.tokens !== null && Boolean(parts.packs);
  const longFigure = figure.length > 6;
  return (
  <>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: both && !longFigure ? 'repeat(2, minmax(0,1fr))' : 'minmax(0,1fr)',
          md: both ? 'repeat(2, minmax(0,1fr))' : 'minmax(0,1fr)'
        },
        gap: large ? { xs: 1.5, md: 2 } : 1.25
      }}
    >
      {parts.tokens !== null && (
        <Box
          sx={{
            ...tileSx(large),
            ...(large && {
              border: '1px solid rgba(242,180,0,0.5)',
              background:
                'radial-gradient(95% 75% at 50% 0%, rgba(242,180,0,0.26), transparent 70%), linear-gradient(180deg, rgba(26,20,6,0.92), rgba(10,12,18,0.96))',
              boxShadow: '0 30px 70px -34px rgba(242,180,0,0.65)'
            })
          }}
        >
          {large && (
            /* the glow behind the coin, breathing */
            <Box
              aria-hidden
              sx={{
                position: 'absolute',
                left: '50%',
                top: { xs: 56, md: 72 },
                width: { xs: 180, md: 240 },
                height: { xs: 180, md: 240 },
                transform: 'translate(-50%, -50%)',
                borderRadius: '50%',
                background:
                  'radial-gradient(circle, rgba(255,211,77,0.38) 0%, rgba(242,180,0,0.12) 45%, transparent 70%)',
                animation: `${pulse} 3.5s ease-in-out infinite`,
                ...STILL,
                pointerEvents: 'none'
              }}
            />
          )}
          {large && (
            <Box sx={{ position: 'relative', mb: { xs: 1.5, md: 2 } }}>
              <Coin size={88} />
            </Box>
          )}
          <Typography
            sx={{
              position: 'relative',
              fontSize: tokenFigureSize(figure, large),
              /* where the browser can measure the tile, the figure fills ~90% of
                 it whatever its length (a bold digit is ~0.62em wide), capped at
                 the design size; the stepped sizes above are the fallback */
              '@supports (width: 1cqw)': {
                fontSize: `min(${large ? 64 : 26}px, ${(145 / Math.max(1, figure.length)).toFixed(1)}cqw)`
              },
              fontWeight: 950,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              ...(large
                ? {
                    /* the figure itself shines: a gold gradient sweeping through the text */
                    backgroundImage:
                      'linear-gradient(100deg, #d08a00 0%, #ffd34d 25%, #fff3b8 45%, #ffd34d 60%, #d08a00 85%, #ffd34d 100%)',
                    backgroundSize: '200% 100%',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    color: 'transparent',
                    WebkitTextFillColor: 'transparent',
                    animation: `${shine} 4s linear infinite`,
                    ...STILL
                  }
                : { color: GOLD })
            }}
          >
            {figure}
          </Typography>
          <Typography
            sx={{
              position: 'relative',
              mt: large ? 1 : 0.75,
              fontSize: large ? { xs: 13, md: 14 } : 14,
              fontWeight: 900,
              // tighter on a phone, where the tile is ~150px wide: one line
              letterSpacing: large ? { xs: '0.12em', md: '0.22em' } : 0,
              textTransform: large ? 'uppercase' : 'none',
              color: large ? GOLD : INK
            }}
          >
            INC tokens
          </Typography>
        </Box>
      )}
      {parts.packs && (
        <Box
          sx={{
            ...tileSx(large),
            ...(large && {
              border: '1px solid rgba(56,232,255,0.4)',
              background:
                'radial-gradient(95% 75% at 50% 15%, rgba(56,232,255,0.2), transparent 70%), linear-gradient(180deg, rgba(8,24,34,0.92), rgba(10,12,18,0.96))',
              boxShadow: '0 30px 70px -34px rgba(56,232,255,0.55)'
            })
          }}
        >
          {parts.packs.imageUrl && (
            /* Pack art is portrait; shown whole, never cropped — floating in a
               cyan glow when it is the focus. Gone, not broken, if it fails:
               the count and name still say what it is. */
            <PackArt
              src={parts.packs.imageUrl}
              alt={parts.packs.name}
              height={large ? { xs: 150, md: 200 } : 72}
              glow={large}
            />
          )}
          {large ? (
            /* built like the tokens tile: the art, the count as the big
               figure, the pack's name as the caption */
            <>
              <Typography
                sx={{
                  position: 'relative',
                  mt: { xs: 1.5, md: 2 },
                  fontSize: { xs: 40, md: 56 },
                  fontWeight: 950,
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  backgroundImage:
                    'linear-gradient(100deg, #d08a00 0%, #ffd34d 25%, #fff3b8 45%, #ffd34d 60%, #d08a00 85%, #ffd34d 100%)',
                  backgroundSize: '200% 100%',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  WebkitTextFillColor: 'transparent',
                  animation: `${shine} 4s linear infinite`,
                  ...STILL
                }}
              >
                {parts.packs.count.toLocaleString('en-AU')} ×
              </Typography>
              <Typography
                sx={{
                  position: 'relative',
                  mt: 1,
                  fontSize: { xs: 13, md: 14 },
                  fontWeight: 900,
                  letterSpacing: { xs: '0.12em', md: '0.22em' },
                  textTransform: 'uppercase',
                  color: CYAN,
                  overflowWrap: 'anywhere'
                }}
              >
                {parts.packs.name}
              </Typography>
            </>
          ) : (
            <Typography
              sx={{
                position: 'relative',
                mt: 0.75,
                fontSize: 14,
                fontWeight: 900,
                color: INK,
                overflowWrap: 'anywhere'
              }}
            >
              {/* in the strip the count leads the name on one line */}
              <Box component="span" sx={{ fontSize: 18, fontWeight: 950, color: GOLD }}>
                {parts.packs.count.toLocaleString('en-AU')} ×{' '}
              </Box>
              {parts.packs.name}
            </Typography>
          )}
        </Box>
      )}
    </Box>
    {parts.custom && large && (
      /* the admin's own reward, a tile like the others, in the winners list's purple */
      <Box
        sx={{
          ...tileSx(true),
          mt: { xs: 1.5, md: 2 },
          py: { xs: 2.5, md: 3 },
          border: '1px solid rgba(201,164,255,0.45)',
          background:
            'radial-gradient(95% 75% at 50% 0%, rgba(201,164,255,0.18), transparent 70%), linear-gradient(180deg, rgba(22,14,36,0.92), rgba(10,12,18,0.96))',
          boxShadow: '0 30px 70px -34px rgba(201,164,255,0.5)'
        }}
      >
        <Typography
          sx={{ position: 'relative', fontSize: 12, fontWeight: 900, letterSpacing: '0.24em', textTransform: 'uppercase', color: '#c9a4ff', mb: 1 }}
        >
          Plus
        </Typography>
        <Typography
          sx={{ position: 'relative', fontSize: { xs: 19, md: 23 }, fontWeight: 900, lineHeight: 1.25, color: INK, overflowWrap: 'anywhere' }}
        >
          {parts.custom}
        </Typography>
      </Box>
    )}
    {parts.custom && !large && (
      <Typography
        sx={{
          mt: 1.5,
          fontSize: 15,
          fontWeight: 800,
          lineHeight: 1.3,
          color: INK,
          textAlign: 'center',
          overflowWrap: 'anywhere'
        }}
      >
        {parts.custom}
      </Typography>
    )}
  </>
  );
};

/* A pack's art that disappears, rather than showing a broken image, if the
 * link fails. `glow` floats it in a cyan halo. */
const PackArt = ({
  src,
  alt,
  height,
  glow = false
}: {
  src: string;
  alt: string;
  height: number | { xs: number; md: number };
  glow?: boolean;
}) => {
  const [failed, setFailed] = useState(false);
  if (failed) return null;
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
      sx={{
        position: 'relative',
        display: 'block',
        mx: 'auto',
        height,
        maxWidth: '100%',
        objectFit: 'contain',
        filter: glow
          ? 'drop-shadow(0 0 22px rgba(56,232,255,0.4)) drop-shadow(0 18px 30px rgba(0,0,0,0.6))'
          : 'drop-shadow(0 14px 26px rgba(0,0,0,0.55))',
        ...(glow && { animation: `${float} 6s ease-in-out infinite`, ...STILL })
      }}
    />
  );
};

/* The page's focus when the admin set no picture, in the picture's place: the
 * tiles set large, or the honest "to be announced". With a picture, the
 * rewards are not shown on the page at all — the picture carries the week. */
const RewardsCard = ({ parts, closesAt }: { parts: RewardParts; closesAt: Date | null }) => (
  <Box
    component="section"
    sx={{
      ...panelSx,
      minWidth: 0,
      p: { xs: 3, md: 4 },
      border: '1px solid rgba(56,232,255,0.26)',
      background:
        'radial-gradient(120% 90% at 0% 0%, rgba(56,232,255,0.10), transparent 60%), linear-gradient(180deg, rgba(15,22,32,0.92), rgba(9,14,21,0.92))',
      boxShadow: '0 30px 80px -44px rgba(56,232,255,0.45)',
      textAlign: 'center'
    }}
  >
    <Typography sx={{ ...kickerSx, mb: 1 }}>This week&apos;s promo draw</Typography>
    <Typography
      component="h2"
      sx={{ fontSize: { xs: 26, md: 34 }, fontWeight: 900, lineHeight: 1.06, color: INK, mb: 1 }}
    >
      Up for grabs
    </Typography>
    <Typography
      sx={{
        mb: { xs: 2.5, md: 3 },
        fontSize: { xs: 14, md: 15 },
        lineHeight: 1.55,
        color: MUTED
      }}
    >
      {DRAW_NOTE}
    </Typography>
    {parts.tokens !== null || parts.packs || parts.custom ? (
      <RewardTiles parts={parts} large />
    ) : (
      <>
        <Typography
          sx={{
            py: { xs: 2, md: 4 },
            fontSize: { xs: 36, md: 52 },
            fontWeight: 950,
            lineHeight: 1.05,
            letterSpacing: '-0.01em',
            color: GOLD
          }}
        >
          To Be Announced
        </Typography>
      </>
    )}
    {closesAt && (
      /* when the pool freezes — the same instant the pack panel names */
      <Typography sx={{ mt: { xs: 2.5, md: 3 }, fontSize: 13, fontWeight: 700, color: MUTED }}>
        Drawn after entries close {formatDrawDate(closesAt)}
      </Typography>
    )}
  </Box>
);

const PromoPackPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  /* Full-screen on a phone, like the funnel's other checkouts. */
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { load, retry } = usePromotion();
  const promotion = load?.status === 'ready' ? load.promotion : null;

  const pack = useMemo(
    () => (promotion ? promoPackItem(promotion) : null),
    [promotion]
  );
  const copy = useMemo(
    () => (promotion ? promoCopy(promotion) : null),
    [promotion]
  );
  // The rewards as tiles: tokens, the pack with its art, the custom line.
  const parts = useMemo(() => rewardParts(promotion?.rewards), [promotion]);
  const [imageFailed, setImageFailed] = useState(false);
  /* The picture's own size once it has loaded: a portrait is laid out as a
     poster, a wide banner spans its column (see the hero). */
  const [imageShape, setImageShape] = useState<{ w: number; h: number } | null>(null);
  /* The last draws' winners, under the pack. */
  const winners = usePromoWinners();

  /* The checkout. `payload` is null until the details step hands it over — the
   * dialog then shows the card step. Closing the dialog drops it, so opening it
   * again starts from the details and a mistyped email can be put right. */
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [payload, setPayload] = useState<RafflePaymentPayload | null>(null);
  /* After paying: the one-time boost, when the flow offers it (see onPaid).
     Holds the paid receipt until the offer is taken or declined. */
  const [upsell, setUpsell] = useState<{ paymentIntentId: string; receipt: PromoPackReceipt } | null>(null);
  /* The dialog's paper is its scroll container. A step change (details → card →
     bonus) swaps its contents but keeps its scroll position, so the bonus offer
     opened wherever the card form had been scrolled to — mid-page on a phone,
     where the form is long. Each step starts at the top. */
  const paperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    paperRef.current?.scrollTo({ top: 0 });
  }, [upsell, payload]);

  /* The A$10,000 giveaway every pack enters, for the hero's count-down. */
  const jackpot = useJackpot();
  const jackpotEnd = jackpot?.endDate ?? null;
  const jackpotDraw = useMemo(() => drawWhen(jackpotEnd), [jackpotEnd]);
  /* When this week's promo draw closes to entries (the admin app's 10 pm Friday). */
  const promoEnd = promotion?.promoDraw?.endDate ?? null;
  const promoClose = useMemo(() => drawWhen(promoEnd), [promoEnd]);

  useEffect(() => {
    track('promo_pack.view');
  }, []);

  const openCheckout = () => {
    if (!pack) return;
    track('promo_pack.checkout_open', { packId: pack.id });
    setPayload(null);
    setCheckoutOpen(true);
  };

  const closeCheckout = () => {
    setCheckoutOpen(false);
    setPayload(null);
  };

  /* Where a paid purchase ends up, with or without the boost: the receipt is
   * written (again, if the boost changed it) and the page hands over to this
   * funnel's own confirmation — never the raffle upsell page. */
  const finish = (receipt: PromoPackReceipt) => {
    savePromoPackReceipt(receipt);
    setUpsell(null);
    closeCheckout();
    navigate('/promo/confirmation', { replace: true, state: { receipt } });
  };

  const shell = (children: ReactNode) => (
    <Box
      sx={{
        background: PAGE_BG,
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <SiteHeader />
      {children}
      <RaffleFooter />
    </Box>
  );

  /* Loading, no promotion this week (a 404), or a read that failed. */
  if (!promotion || !pack || !copy) {
    return shell(
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2.5,
          py: { xs: 8, md: 12 }
        }}
      >
        {load === null ? (
          <CircularProgress
            sx={{ color: CYAN }}
            aria-label="Loading the promo pack"
          />
        ) : (
          <Box
            sx={{
              ...panelSx,
              p: { xs: 3, md: 4.5 },
              maxWidth: 520,
              textAlign: 'center'
            }}
          >
            <Typography sx={{ ...kickerSx, mb: 1.5 }}>Promo pack</Typography>
            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 29, md: 38 },
                fontWeight: 900,
                lineHeight: 1.12,
                color: INK,
                mb: 2
              }}
            >
              {load.status === 'none'
                ? 'There is no promo pack this week'
                : 'We could not load the promo pack'}
            </Typography>
            <Typography
              sx={{
                fontSize: { xs: 15.5, md: 16.5 },
                color: BODY,
                lineHeight: 1.65
              }}
            >
              {load.status === 'none'
                ? 'Check back soon. In the meantime, the giveaway has its own packs.'
                : 'Please check your connection and try again.'}
            </Typography>
            {load.status === 'none' ? (
              <Button component={Link} to="/giveaway" sx={{ ...ctaSx, mt: 3 }}>
                Go to the giveaway
              </Button>
            ) : (
              <Button onClick={retry} sx={{ ...ctaSx, mt: 3 }}>
                Try again
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  }

  /* The admin's picture is the focus when there is one; without it — or if it
     fails to load — this week's rewards take its place. */
  const showImage = Boolean(copy.imageUrl) && !imageFailed;
  /* A picture taller than it is wide — the admin's pictures are phone-made
     posters — once it has loaded and said so. Null for a banner, or until the
     picture loads. */
  const portrait = showImage && imageShape && imageShape.h > imageShape.w ? imageShape : null;
  const price = Number(pack.price);
  /* What Stripe's Elements is told, in cents: the pack's price. Exactly as
   * RafflePaymentPage mounts its one-off checkout — there the page's own
   * `bonusSelected` stays false while the form is open, so ticking the form's
   * "Bonus add-on" never changes this amount or remounts the card field. The
   * server prices the intent itself, from the package and `hasBonus`. */
  const amountCents = Math.round(price * 100);
  /* Bound to a const so TypeScript narrows it inside the card step's onPaid. */
  const detailsPayload = payload;

  /* Whether the week's rewards are set — the same test the rewards card
     makes. The jackpot countdown shows only once they are (2026-10-14, the
     owner's call). */
  const rewardsAnnounced = parts.tokens !== null || parts.packs !== null || Boolean(parts.custom);
  /* The jackpot's draw, when there is one to show: a countdown under 30 days,
     else the date. Honest when there is nothing to count down to — the timer
     has no closed state and would sit on 00:00:00:00. */
  const jackpotWhen =
    rewardsAnnounced && (jackpotDraw.kind === 'countdown' || jackpotDraw.kind === 'date') ? jackpotDraw : null;

  return shell(
    <Box sx={{ flex: 1 }}>
      {/* ── the focus, headline, price, buy ───────────────────────────────
          The focus is the admin's picture when there is one, else this week's
          rewards, set large in its place. */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 4.5, md: 8 },
          pb: showImage ? { xs: 3, md: 4 } : { xs: 5, md: 7 },
          display: 'grid',
          /* A portrait is a poster, and the poster is the page: shown big, at
             a set height, as wide as that makes it, with the words beside it
             and the pair centred — not a narrow picture adrift in a wide
             column. fit-content keeps a long headline inside the page. A
             banner (not expected) takes the wider column instead. */
          gridTemplateColumns: {
            xs: 'minmax(0,1fr)',
            md: portrait ? 'auto fit-content(620px)' : 'minmax(0,1.1fr) minmax(0,0.9fr)'
          },
          justifyContent: portrait ? 'center' : 'normal',
          gap: { xs: 3.5, md: 6 },
          alignItems: 'center'
        }}
      >
        {showImage ? (
          /* The picture at its own proportions, never cropped — it can carry
             text — and the frame hugs it, so a portrait gets no empty bands.
             Gone, not broken, if it fails: the rewards card takes its place. */
          <Box
            component="img"
            src={copy.imageUrl}
            alt={copy.headline}
            onLoad={(e) => {
              const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
              setImageShape({ w, h });
            }}
            onError={() => setImageFailed(true)}
            sx={{
              display: 'block',
              justifySelf: 'center',
              width: 'auto',
              /* A poster on a phone fills the width (a tall one stops short of
                 the whole screen); from md up it stands 760px tall, never
                 upscaled, and a squat one is held to under half the viewport
                 so the words beside it keep their room. */
              maxWidth: portrait ? { xs: '100%', md: '46vw' } : '100%',
              height: portrait ? { xs: 'auto', md: Math.min(760, portrait.h) } : 'auto',
              maxHeight: portrait ? { xs: 'min(640px, 78vh)', md: 'none' } : { xs: 320, md: 460 },
              borderRadius: '18px',
              border: `1px solid ${LINE}`,
              background: 'rgba(255,255,255,0.02)'
            }}
          />
        ) : (
          <RewardsCard
            parts={parts}
            closesAt={promoClose.kind === 'countdown' || promoClose.kind === 'date' ? promoClose.at : null}
          />
        )}

        {/* The pack in a panel of its own (2026-10-14), lit like the reward
            tiles — a cyan glow at the top, the button's green at the foot — and
            built to be bought from: the kicker, the admin's headline and blurb,
            what one pack gives as three chips (cards inside, the promo-draw
            entry, the giveaway entries), the price with "per pack", the button
            across the panel's width, and when this week's draw closes. */}
        <Box
          sx={{
            ...panelSx,
            position: 'relative',
            overflow: 'hidden',
            p: { xs: 3, md: 4 },
            minWidth: 0,
            textAlign: 'center',
            border: '1px solid rgba(56,232,255,0.22)',
            boxShadow: '0 0 0 1px rgba(56,232,255,0.05), 0 34px 90px -36px rgba(56,232,255,0.45)',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
              background:
                'radial-gradient(62% 48% at 50% 0%, rgba(56,232,255,0.17), transparent 72%), radial-gradient(55% 42% at 50% 100%, rgba(52,211,153,0.16), transparent 72%)'
            },
            '& > *': { position: 'relative' }
          }}
        >
          {/* The admin's headline, else the pack's name. Admin text throughout:
              React text only, and free to break anywhere on a narrow phone. */}
          <Typography
            component="h1"
            sx={{
              fontSize: { xs: 32, md: 44 },
              fontWeight: 900,
              lineHeight: 1.06,
              color: INK,
              mb: copy.blurb ? 1.5 : 0,
              overflowWrap: 'anywhere'
            }}
          >
            {copy.headline}
          </Typography>

          {copy.blurb && (
            <Typography
              sx={{
                fontSize: { xs: 15.5, md: 16.5 },
                color: BODY,
                lineHeight: 1.6,
                maxWidth: 520,
                mx: 'auto',
                overflowWrap: 'anywhere'
              }}
            >
              {copy.blurb}
            </Typography>
          )}

          {/* What one pack gives, as figures. Row one: the cards and the
              promo-draw entry (every pack is one — DRAW_NOTE's rule). Row two:
              the giveaway entries. Row three: the jackpot's draw — the
              countdown, or the date — on a row of its own, shown only once the
              week's rewards are set. */}
          <Box sx={{ mt: 3, borderTop: `1px solid ${LINE}`, borderBottom: `1px solid ${LINE}` }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${pack.nfts > 0 ? 2 : 1}, minmax(0, 1fr))` }}>
              {pack.nfts > 0 && (
                <PackFigure colour={CYAN} figure={pack.nfts.toLocaleString('en-AU')} label="cards inside" />
              )}
              <PackFigure colour={GOLD} figure="1" label="entry in the promo draw" />
            </Box>
            {pack.entries > 0 && (
              <Box sx={{ borderTop: `1px solid ${LINE}` }}>
                <PackFigure
                  colour={GOLD}
                  figure={`+${pack.entries.toLocaleString('en-AU')}`}
                  label={`${plural(pack.entries, 'entry', 'entries').replace(/^[\d,]+ /, '')} in the ${
                    jackpot?.prizeAmount ? `A$${jackpot.prizeAmount.toLocaleString('en-AU')}` : 'A$10,000'
                  } giveaway`}
                />
                {jackpotWhen && (
                  <Box
                    sx={{
                      px: { xs: 1, md: 1.5 },
                      py: { xs: 1.5, md: 1.75 },
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      minWidth: 0,
                      pt: 0
                    }}
                  >
                    <Typography sx={{ ...labelSx, mb: 1 }}>{jackpotWhen.kind === 'countdown' ? 'DRAWN IN' : 'DRAWN ON'}</Typography>
                    {jackpotWhen.kind === 'countdown' ? (
                      <GiveawayCountdownTimer nextGiveaway={jackpotWhen.at} isDense />
                    ) : (
                      <Typography sx={{ fontSize: 15, fontWeight: 800, color: INK }}>{formatDrawDate(jackpotWhen.at)}</Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 1.25 }}>
            <Typography sx={{ fontSize: { xs: 42, md: 50 }, fontWeight: 950, color: GOLD, lineHeight: 1 }}>
              {formatAud(price)}
            </Typography>
            <Typography sx={{ fontSize: 12.5, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: MUTED }}>
              per pack
            </Typography>
          </Box>

          {/* The one thing to do on the page: green, so it is not one more gold
              figure, bigger than the funnel's other buttons, lit, and as wide as
              the panel. */}
          <Button
            onClick={openCheckout}
            sx={{
              ...ctaSx,
              mt: 3,
              width: '100%',
              px: 5,
              py: 2,
              fontSize: 20,
              color: '#04120b',
              background: `linear-gradient(180deg, #5eead4 0%, ${BUY_GREEN} 100%)`,
              boxShadow: `0 0 0 1px rgba(52,211,153,0.35), 0 18px 44px -12px rgba(52,211,153,0.85)`,
              '&:hover': {
                background: `linear-gradient(180deg, #7ff0dd 0%, #3ee0a8 100%)`,
                boxShadow: `0 0 0 1px rgba(52,211,153,0.5), 0 22px 50px -12px rgba(52,211,153,0.95)`
              }
            }}
          >
            Buy now
          </Button>

          {/* When this week's draw closes — the API's promo draw end, the same
              instant the admin app closes it to entries; nothing when none is
              open or it has passed. */}
          {(promoClose.kind === 'countdown' || promoClose.kind === 'date') && (
            <Typography sx={{ mt: 1.75, fontSize: 13, fontWeight: 700, color: MUTED }}>
              This week&apos;s draw closes {formatDrawDate(promoClose.at)}
            </Typography>
          )}
        </Box>
      </Box>

      {/* ── previous promo winners ─────────────────────────────────── */}
      <PreviousPromoWinners draws={winners} />

      {/* ── where the draw is watched, and the community ────────────────── */}
      <Box
        sx={{
          maxWidth: 900,
          mx: 'auto',
          px: { xs: 2.5, md: 4 },
          pt: { xs: 5, md: 7 },
          pb: { xs: 6, md: 9 },
          textAlign: 'center'
        }}
      >
        <Typography component="h2" sx={{ ...kickerSx, mb: 2 }}>
          Follow along
        </Typography>
        <GiveawayOfferSocialLinks page="promo-pack" />
      </Box>

      {/* ── the checkout ─────────────────────────────────────────────────────
          Mounted only while open, as the QR page's is: closing clears the
          details, and a dialog fading out would show the details step under the
          card step on its way out. */}
      {checkoutOpen && (
        <Dialog
          open
          fullScreen={isSmallScreen}
          maxWidth={false}
          onClose={(event, reason) => {
            /* A form full of details, or a card form, is not something to lose
               to a stray click — the same rule as the funnel's other checkouts. */
            if (reason === 'backdropClick') return;
            if (reason === 'escapeKeyDown') return;
            closeCheckout();
          }}
          PaperProps={{
            ref: paperRef,
            sx: {
              width: isSmallScreen ? '100%' : '50vw',
              maxWidth: isSmallScreen ? '100%' : 760,
              borderRadius: isSmallScreen ? 0 : 4,
              background: PAGE_BG,
              border: `1px solid ${LINE}`,
              boxShadow: '0 24px 64px rgba(0,0,0,0.55)'
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${LINE}`
            }}
          >
            <Typography sx={{ fontWeight: 950, color: INK, fontSize: 16 }}>
              {upsell
                ? 'One more thing'
                : detailsPayload
                  ? 'Checkout'
                  : 'Your details'}
            </Typography>
            <Box sx={{ ml: 'auto' }}>
              <IconButton
                aria-label="Close checkout"
                /* closing on the offer declines it — the pack is already paid for */
                onClick={() =>
                  upsell ? finish(upsell.receipt) : closeCheckout()
                }
                sx={{ color: MUTED, '&:hover': { color: INK } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 2, sm: 3 } }}>
            {upsell && (
              /* The one-time boost, as the giveaway site and the portal offer
                 it after a pack is paid for. Taken or declined, the page moves
                 on to the receipt. */
              <PromoUpsellStep
                paymentIntentId={upsell.paymentIntentId}
                onDone={(applied) =>
                  finish(
                    applied ? withUpsell(upsell.receipt, applied) : upsell.receipt
                  )
                }
              />
            )}

            {!upsell && !detailsPayload && (
              /* The details step. It creates no account and probes no address;
               `persistToSession` is off so this page's payload never turns up
               as the raffle payment page's fallback. */
              <RaffleEntryForm
                packageName={pack.title}
                surface="dark"
                selectedPack={pack}
                standaloneItems={[pack]}
                subscriptionItems={[]}
                persistToSession={false}
                signupSource="promo-pack"
                onReadyToPay={(details) => setPayload(details)}
              />
            )}

            {!upsell && detailsPayload && (
              <Elements
                key={`standalone:${amountCents}:on_session`}
                stripe={stripePromise}
                options={{
                  mode: 'payment',
                  currency: 'aud',
                  paymentMethodTypes: ['card'],
                  setupFutureUsage: 'on_session',
                  amount: amountCents,
                  appearance: ELEMENTS_APPEARANCE
                }}
              >
                <RaffleStripeCheckoutForm
                  payload={detailsPayload}
                  surface="dark"
                  mode="standalone"
                  pack={pack}
                  subDiscount={SUBSCRIPTION_DISCOUNT_PERCENT}
                  onPaid={({
                    paymentIntentId,
                    bonusSelected,
                    accountCreated
                  }) => {
                    const receipt = promoPackReceipt({
                      entry: detailsPayload.entry,
                      pack,
                      bonusSelected,
                      accountCreated,
                      paymentIntentId
                    });
                    savePromoPackReceipt(receipt);
                    track('promo_pack.purchase', {
                      packId: pack.id,
                      bonusSelected: receipt.bonusSelected,
                      valueAud: receipt.amountPaidAud,
                      accountCreated: receipt.accountCreated
                    });
                    /* The one-time boost, on the giveaway site's and the
                       portal's rule: offered when the pack has no bonus or the
                       bonus was added, and only while the token that
                       authorises the charge is held (utils/upsellToken).
                       Otherwise straight to this funnel's receipt. */
                    const offerBoost =
                      (!pack.bonus || receipt.bonusSelected) &&
                      Boolean(paymentIntentId) &&
                      Boolean(recallUpsellToken(paymentIntentId));
                    if (offerBoost && paymentIntentId) {
                      setUpsell({ paymentIntentId, receipt });
                      return;
                    }
                    finish(receipt);
                  }}
                />
              </Elements>
            )}
          </Box>
        </Dialog>
      )}
    </Box>
  );
};

export default PromoPackPage;
/* The reward tiles, for the member portal's purchase modal — the same tiles
   as this page's rewards card. */
export { RewardTiles as PromoRewardTiles };
